import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
} from "react-router-dom";
import MainLayout from "layouts/MainLayout";
import AuthLayout from "layouts/AuthLayout";
import GuestRoute from "routes/GuestRoute";
import ProtectedRoute from "routes/ProtectedRoute";
import {
  publicRoutes,
  authRoutes,
  publicAuthRoutes,
  privateRoutes,
} from "routes/routes";
import NotFoundPage from "pages/NotFoundPage";
import SettingsLayout from "pages/settings/SettingsLayout";
import SettingsProfilePage from "pages/settings/SettingsProfilePage";
import SettingsChangeImagePage from "pages/settings/SettingsChangeImagePage";
import SettingsChangePasswordPage from "pages/settings/SettingsChangePasswordPage";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { setIndustries } from "store/reducer/configData";
import { LogoutUser } from "store/reducer";
import { registerSessionExpiredHandler } from "lib/sessionExpired";
import { AcknowledgementModal } from "components/acknowledgement-modal";
import { useScrollToTopOnRouteChange } from "hooks/useScrollToTopOnRouteChange";

function normalizeIndustriesPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.industries)) return payload.industries;
  return [];
}

async function fetchIndustries(dispatch) {
  try {
    const resp = await api.get(API_ENDPOINTS.industries, { auth: false });
    if (!isSuccessResponse(resp)) {
      dispatch(setIndustries([]));
      return;
    }
    dispatch(setIndustries(normalizeIndustriesPayload(resp?.data ?? resp)));
  } catch {
    dispatch(setIndustries([]));
  }
}
//
function AppRoutes() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadedRef = useRef(false);

  useScrollToTopOnRouteChange();

  useEffect(() => {
    registerSessionExpiredHandler(() => {
      dispatch(LogoutUser());
      navigate("/sign-in", { replace: true });
    });
    return () => registerSessionExpiredHandler(null);
  }, [dispatch, navigate]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchIndustries(dispatch);
  }, [dispatch]);

  return (
    <>
      <AcknowledgementModal />
      <Routes>
        <Route element={<AuthLayout />}>
          <Route element={<GuestRoute />}>
            {authRoutes.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>
          {publicAuthRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>

        <Route element={<MainLayout />}>
          {publicRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          <Route
            path="/investor-agreement"
            element={<Navigate to="/investor-overview" replace />}
          />

          <Route element={<ProtectedRoute />}>
            <Route path="/portfolio" element={<Navigate to="/dashboard" replace />} />
            {privateRoutes.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<SettingsProfilePage />} />
              <Route path="image" element={<SettingsChangeImagePage />} />
              <Route path="password" element={<SettingsChangePasswordPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return <AppRoutes />;
}
